import React from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { differenceInDays, parseISO, format } from 'date-fns';

const { FiCalendar, FiAlertCircle, FiX, FiMail, FiArrowRight } = FiIcons;

const NotificationPanel = ({ properties, onClose }) => {
  const navigate = useNavigate();
  const today = new Date();

  const notifications = properties
    .filter(p => p.leaseEnd)
    .map(p => {
      const daysLeft = differenceInDays(parseISO(p.leaseEnd), today);
      let status = 'upcoming';
      if (daysLeft < 0) status = 'expired';
      else if (daysLeft <= 14) status = 'urgent';
      else if (daysLeft <= 30) status = 'warning';
      else return null;

      return { ...p, daysLeft, status };
    })
    .filter(Boolean)
    .sort((a, b) => a.daysLeft - b.daysLeft);

  const getStatusStyles = (status) => {
    switch (status) {
      case 'expired': return 'bg-black text-white';
      case 'urgent': return 'bg-red-100 text-red-700 border-red-200';
      case 'warning': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  return (
    <div className="absolute right-0 mt-2 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
      <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          <SafeIcon icon={FiCalendar} className="text-indigo-600" />
          Lease Alerts ({notifications.length})
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <SafeIcon icon={FiX} />
        </button>
      </div>

      <div className="max-h-[400px] overflow-y-auto divide-y divide-gray-50">
        {notifications.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <SafeIcon icon={FiCalendar} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No urgent lease expirations.</p>
          </div>
        ) : (
          notifications.map(n => (
            <div key={n.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="text-sm font-bold text-gray-900">{n.name}</h4>
                  <p className="text-xs text-gray-500">{n.tenantName}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase border ${getStatusStyles(n.status)}`}>
                  {n.status === 'expired' ? 'Expired' : `${n.daysLeft} Days`}
                </span>
              </div>
              
              <div className="flex items-center justify-between mt-3">
                <div className="flex gap-2">
                  <button 
                    onClick={() => window.location.href = `mailto:${n.tenantEmail}?subject=Lease Renewal - ${n.name}`}
                    className="p-1.5 bg-white border border-gray-200 rounded-lg text-gray-500 hover:text-indigo-600 hover:border-indigo-200 transition"
                    title="Email Tenant"
                  >
                    <SafeIcon icon={FiMail} size={14} />
                  </button>
                </div>
                <button 
                  onClick={() => {
                    navigate('/properties');
                    onClose();
                  }}
                  className="text-xs font-bold text-indigo-600 flex items-center hover:underline"
                >
                  Manage <SafeIcon icon={FiArrowRight} className="ml-1" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-100 bg-gray-50 text-center text-[11px] text-gray-400">
          Tip: Send renewal notices 30-45 days in advance.
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;